import type { JSX } from "react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Plus, ShoppingCart } from "lucide-react";
import { useLiveQuery } from "dexie-react-hooks";
import type { ShoppingListApiItem } from "@recipevault/shared";
import { db } from "../../sync/db.js";
import { useSync } from "../../sync/useSync.js";
import { Button } from "@/ui/button.js";
import { Badge } from "@/ui/badge.js";
import { CreateListSheet } from "./CreateListSheet.js";

export function ShoppingListsIndex(): JSX.Element {
  const { service } = useSync();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);

  const lists = useLiveQuery(
    () => db.shoppingLists.orderBy("updatedAt").reverse().toArray(),
    [],
    [] as ShoppingListApiItem[],
  );

  // Active lists first, then archived; within each group already newest-first
  const sorted = (lists ?? []).slice().sort((a, b) => {
    if (a.status === b.status) return 0;
    return a.status === "active" ? -1 : 1;
  });

  const isEmpty = lists !== undefined && lists.length === 0;

  async function handleCreate(name: string, recipeIds: string[]): Promise<void> {
    const list = await service.createShoppingListOptimistic({
      name,
      ...(recipeIds.length > 0 ? { recipeIds } : {}),
    });
    setSheetOpen(false);
    void navigate(`/shopping/${list.id}`);
  }

  return (
    <>
      <header className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Shopping</h1>
        {!isEmpty && (
          <Button
            size="sm"
            onClick={() => {
              setSheetOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            New list
          </Button>
        )}
      </header>

      {isEmpty && (
        <div className="py-12 flex flex-col items-center justify-center text-center">
          <ShoppingCart className="h-12 w-12 text-zinc-600 mb-4" aria-hidden />
          <div className="text-zinc-300 text-lg font-medium mb-2">No active list yet</div>
          <p className="text-zinc-500 text-sm max-w-sm mb-6">
            Create a shopping list to track what you need to buy.
          </p>
          <Button
            onClick={() => {
              setSheetOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            New list
          </Button>
        </div>
      )}

      {sorted.length > 0 && (
        <div className="flex flex-col gap-2">
          {sorted.map((list) => {
            const total = list.items.length;
            const unchecked = list.items.filter((i) => !i.checked).length;
            return (
              <Link key={list.id} to={`/shopping/${list.id}`}>
                <div className="flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-900 px-4 py-3 hover:bg-zinc-800/60 transition-colors cursor-pointer">
                  <div className="min-w-0">
                    <p className="font-medium text-zinc-100 truncate">{list.name}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {total === 0
                        ? "No items"
                        : `${unchecked} of ${total} remaining`}
                    </p>
                  </div>
                  {list.status === "archived" && (
                    <Badge variant="secondary" className="ml-3 shrink-0">
                      Archived
                    </Badge>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <CreateListSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onSubmit={handleCreate}
      />
    </>
  );
}
